// Carry Roll OptimizeLead — leads the Optimize screen with the single highest-
// value gain opportunity (progressive disclosure: answer first, machinery after).

import { useMemo } from 'react';
import { TrendingUp, Check } from 'lucide-react';
import { useRequest } from '../../store/selectors';
import { fixablePriority } from '@carry-roll/shared-logic';
import { convertDistance } from '@carry-roll/shared-logic';
import { useStore } from '../../store';

export function OptimizeLead() {
  const request = useRequest();
  const distanceUnit = useStore((s) => s.distanceUnit);
  const items = useMemo(() => fixablePriority(request), [request]);

  const top = items[0];
  const conv = (m: number) => Math.round(convertDistance(m, distanceUnit));

  if (!top) {
    return (
      <div
        data-testid="optimize-lead"
        className="panel"
        role="status"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          borderColor: 'var(--color-success)',
        }}
      >
        <Check size={24} aria-hidden="true" style={{ color: 'var(--color-success)', flexShrink: 0 }} />
        <div>
          <div className="text-sm" style={{ fontWeight: 'var(--fw-medium)' }}>
            Your launch is well-matched to this club.
          </div>
          <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            All key inputs are inside their reference bands — no quick gains available.
          </div>
        </div>
      </div>
    );
  }

  const direction = top.deviation > 0 ? 'lower' : 'raise';
  const amount = `${Math.abs(top.deviation)} ${top.unit}`;
  const gain = conv(top.expectedGainM);

  return (
    <div
      data-testid="optimize-lead"
      className="panel"
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        borderColor: 'var(--color-accent)',
      }}
    >
      <TrendingUp size={24} aria-hidden="true" style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
      <div>
        <div className="text-sm" style={{ fontWeight: 'var(--fw-medium)' }}>
          To gain{' '}
          <span style={{ color: 'var(--color-success)', fontWeight: 'var(--fw-bold)' }}>
            +{gain} {distanceUnit}
          </span>{' '}
          carry, {direction} {top.parameter} by {amount}.
        </div>
        <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          The biggest correctable lever for your current launch. Open the sections below to back-solve, tune, and explore.
        </div>
      </div>
    </div>
  );
}
